import { create } from 'zustand'

import type { Notification, NotificationsList } from '@/types/api/notification'

/**
 * The bell's view (current workspace plus invites), loaded page by page, and the polled unread
 * counts. `unreadCount` covers the whole view, not only the loaded pages.
 */
interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  hasMore: boolean
  /** Unread notifications per workspace (invites excluded), for the workspace selector. */
  unreadByWorkspace: Record<string, number>
  isLoading: boolean

  /** Replace the list with its first page. */
  setFirstPage: (page: NotificationsList) => void
  /** Append a later page (deduplicated, in case new notifications shifted the pages). */
  appendPage: (page: NotificationsList) => void
  setSummary: (unreadCount: number, unreadByWorkspace: Record<string, number>) => void
  markAllRead: (workspaceId: string | null) => void
  /** Marks one notification read (after it was opened). */
  markRead: (id: string) => void
  remove: (id: string) => void
  setLoading: (loading: boolean) => void
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  hasMore: false,
  unreadByWorkspace: {},
  isLoading: false,

  setFirstPage: (page) =>
    set({ notifications: page.notifications, unreadCount: page.unreadCount, hasMore: page.hasMore }),

  appendPage: (page) =>
    set((state) => {
      const known = new Set(state.notifications.map((n) => n.id))
      return {
        notifications: [...state.notifications, ...page.notifications.filter((n) => !known.has(n.id))],
        unreadCount: page.unreadCount,
        hasMore: page.hasMore,
      }
    }),

  setSummary: (unreadCount, unreadByWorkspace) => set({ unreadCount, unreadByWorkspace }),

  markAllRead: (workspaceId) =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
      unreadByWorkspace: workspaceId ? { ...state.unreadByWorkspace, [workspaceId]: 0 } : state.unreadByWorkspace,
    })),

  markRead: (id) =>
    set((state) => {
      const target = state.notifications.find((n) => n.id === id)
      if (!target || target.read) return state
      return {
        notifications: state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }
    }),

  remove: (id) =>
    set((state) => {
      const removed = state.notifications.find((n) => n.id === id)
      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        unreadCount: removed && !removed.read ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      }
    }),

  setLoading: (loading) => set({ isLoading: loading }),
}))

/** Unread notification counts keyed by workspace id (from the 30s summary poll). */
export function useUnreadCountsByWorkspace(): Record<string, number> {
  return useNotificationStore((state) => state.unreadByWorkspace)
}
